import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import { LandingPage } from "./pages/LandingPage";
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { DashboardPage } from "./pages/DashboardPage";
import { CalculatorsPage } from "./pages/CalculatorsPage";
import { FundResearchPage } from "./pages/FundResearchPage";
import { FundDetailPage } from "./pages/FundDetailPage";
import { CompareFundsPage } from "./pages/CompareFundsPage";
import { PortfolioPage } from "./pages/PortfolioPage";
import { BlogPage } from "./pages/BlogPage";
import { BlogPostPage } from "./pages/BlogPostPage";
import { ContactPage } from "./pages/ContactPage";
import { WatchlistPage } from "./pages/WatchlistPage";
import { AdminPage } from "./pages/AdminPage";
import { PriceAlertsPage } from "./pages/PriceAlertsPage";
import { SIPRemindersPage } from "./pages/SIPRemindersPage";
import { AppointmentsPage } from "./pages/AppointmentsPage";
import { TopPerformingFundsPage } from "./pages/TopPerformingFundsPage";
import { FundScreenerPage } from "./pages/FundScreenerPage";
import { RealReturnsCalculatorPage } from "./pages/RealReturnsCalculatorPage";
// Phase 2 & 3 - Advanced Analytics
import { ConsistentPerformersPage } from "./pages/ConsistentPerformersPage";
import { PointToPointPage } from "./pages/PointToPointPage";
import { FDvsDebtPage } from "./pages/FDvsDebtPage";
import { FundFactsheetPage } from "./pages/FundFactsheetPage";
import { AMCAUMPage } from "./pages/AMCAUMPage";
// Research Tools Hub
import { ResearchToolsPage } from "./pages/ResearchToolsPage";
import { 
  SIPCalculatorPage, 
  LumpsumCalculatorPage, 
  SWPCalculatorPage,
  STPCalculatorPage,
  TopFundsAMCPage,
  TrailingReturnsPage,
  PPFvsELSSPage,
  LiquidVsSavingsPage,
  AnnualReturnsPage,
  QuartileRankingPage,
  VolatilityRankingPage,
  HighestDividendPage,
  PortfolioOverlapPage
} from "./pages/research";
// BSE StARMF - Distributor Dashboard
import { DistributorDashboardPage } from "./pages/DistributorDashboardPage";
import { TransactionsPage } from "./pages/TransactionsPage";
import { SIPManagementPage } from "./pages/SIPManagementPage";
import { ClientDetailsPage } from "./pages/ClientDetailsPage";
import { ReportsPage } from "./pages/ReportsPage";
import { CASImportPage } from "./pages/CASImportPage";
import { ClientDashboardPage } from "./pages/ClientDashboardPage";
// Investor Portal
import { InvestorLoginPage } from "./pages/InvestorLoginPage";
import { InvestorDashboardPage } from "./pages/InvestorDashboardPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // Check both window.location.hash and window.location.href for session_id
    const fullUrl = window.location.href;
    if (fullUrl.includes('session_id=') || window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/auth/me`, {
        headers,
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      setToken(null);
      localStorage.removeItem("token");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error("Logout error:", e);
    }
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const setAuthUser = (userData, authToken) => {
    if (authToken) {
      localStorage.setItem("token", authToken);
      setToken(authToken);
    }
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, loading, token, login, register, logout, setAuthUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Try to get session_id from both location.hash and window.location.href
      let sessionId = null;
      const hash = location.hash || window.location.hash;
      const fullUrl = window.location.href;
      
      // Try hash first
      if (hash && hash.includes('session_id=')) {
        sessionId = new URLSearchParams(hash.replace('#', '?')).get('session_id');
      }
      
      // If not found in hash, try the full URL (in case it's after # in the path)
      if (!sessionId && fullUrl.includes('session_id=')) {
        const hashPart = fullUrl.split('#')[1];
        if (hashPart) {
          sessionId = new URLSearchParams(hashPart.replace('#', '?')).get('session_id');
        }
      }
      
      if (sessionId) {
        try {
          const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
          setAuthUser(response.data);
          toast.success("Welcome to Paisa Secure!");
          navigate('/dashboard', { replace: true, state: { user: response.data } });
        } catch (error) {
          console.error("Auth error:", error);
          toast.error("Authentication failed. Please try again.");
          navigate('/login', { replace: true });
        }
      } else {
        console.error("No session_id found in callback URL");
        toast.error("Authentication failed - no session ID");
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [location, navigate, setAuthUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  // Check both location.hash and window.location.href for session_id
  const fullUrl = window.location.href;
  if (fullUrl.includes('session_id=') || location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/calculators" element={<CalculatorsPage />} />
      <Route path="/research" element={<FundResearchPage />} />
      <Route path="/research/:schemeCode" element={<FundDetailPage />} />
      <Route path="/compare" element={<CompareFundsPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/appointments" element={<AppointmentsPage />} />
      {/* MF Research Tools - Phase 1 */}
      <Route path="/top-performers" element={<TopPerformingFundsPage />} />
      <Route path="/screener" element={<FundScreenerPage />} />
      <Route path="/returns-calculator" element={<RealReturnsCalculatorPage />} />
      {/* MF Research Tools - Phase 2 & 3 */}
      <Route path="/consistent-performers" element={<ConsistentPerformersPage />} />
      <Route path="/point-to-point" element={<PointToPointPage />} />
      <Route path="/fd-vs-debt" element={<FDvsDebtPage />} />
      <Route path="/factsheet/:schemeCode" element={<FundFactsheetPage />} />
      <Route path="/amc-aum" element={<AMCAUMPage />} />
      {/* Research Tools Hub */}
      <Route path="/research-tools" element={<ResearchToolsPage />} />
      <Route path="/research/sip-calculator" element={<SIPCalculatorPage />} />
      <Route path="/research/lumpsum-calculator" element={<LumpsumCalculatorPage />} />
      <Route path="/research/swp-calculator" element={<SWPCalculatorPage />} />
      <Route path="/research/stp-calculator" element={<STPCalculatorPage />} />
      <Route path="/research/top-funds-amc" element={<TopFundsAMCPage />} />
      <Route path="/research/trailing-returns" element={<TrailingReturnsPage />} />
      <Route path="/research/ppf-vs-elss" element={<PPFvsELSSPage />} />
      <Route path="/research/liquid-vs-savings" element={<LiquidVsSavingsPage />} />
      <Route path="/research/annual-returns" element={<AnnualReturnsPage />} />
      <Route path="/research/quartile-ranking" element={<QuartileRankingPage />} />
      <Route path="/research/volatility-ranking" element={<VolatilityRankingPage />} />
      <Route path="/research/highest-dividend" element={<HighestDividendPage />} />
      <Route path="/research/portfolio-overlap" element={<PortfolioOverlapPage />} />
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/portfolio" element={<ProtectedRoute><PortfolioPage /></ProtectedRoute>} />
      <Route path="/watchlist" element={<ProtectedRoute><WatchlistPage /></ProtectedRoute>} />
      <Route path="/alerts" element={<ProtectedRoute><PriceAlertsPage /></ProtectedRoute>} />
      <Route path="/sip-reminders" element={<ProtectedRoute><SIPRemindersPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
      {/* BSE StARMF - Distributor Dashboard */}
      <Route path="/distributor" element={<ProtectedRoute><DistributorDashboardPage /></ProtectedRoute>} />
      <Route path="/distributor/transactions" element={<ProtectedRoute><TransactionsPage /></ProtectedRoute>} />
      <Route path="/distributor/sip" element={<ProtectedRoute><SIPManagementPage /></ProtectedRoute>} />
      <Route path="/distributor/client/:clientId" element={<ProtectedRoute><ClientDetailsPage /></ProtectedRoute>} />
      <Route path="/distributor/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/distributor/cas-import" element={<ProtectedRoute><CASImportPage /></ProtectedRoute>} />
      <Route path="/distributor/clients" element={<ProtectedRoute><ClientDashboardPage /></ProtectedRoute>} />
      {/* Investor Portal (separate auth) */}
      <Route path="/investor/login" element={<InvestorLoginPage />} />
      <Route path="/investor/dashboard" element={<InvestorDashboardPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster 
            position="top-right" 
            toastOptions={{
              style: {
                background: 'hsl(222 47% 7%)',
                border: '1px solid hsl(217 33% 17%)',
                color: 'hsl(210 40% 98%)',
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
